import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Paper, Typography } from "@mui/material";
import { NextStep, PageHeader } from "@components/ui_v2";
import iconCheckCircleSuccess from "@assets/img/icon_check_circle_success.svg";
import { RootState } from "@store/store";
import { formatPhoneNumber } from "@helpers/funciones";
import { useLazyGetLoanApplicationByIdQuery } from "@api/applications";
import useInterval from "@hooks/useInterval";

const SECONDS_TO_FETCH = 10;

/** Tipo de equipo de ejemplo del diseño; el catálogo de leasing aún no se lee en esta pantalla. */
const EQUIPO_DEFAULT = "equipo médico";

/**
 * Cierre del flujo de Mercader (`S_ContactoEjecutivo` del diseño): el expediente quedó completo y un
 * ejecutivo continúa el proceso, así que no hay firma en línea.
 */
export const RevisionExpedientePasos = () => {
  const navigate = useNavigate();
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const application = useSelector((state: RootState) => state.register.application);
  const loanStatus = useSelector((state: RootState) => state.register.loan_status);
  const [getLoanApplication] = useLazyGetLoanApplicationByIdQuery();

  const statusPoll = useInterval(async () => {
    try {
      const loanApplication = await getLoanApplication(application.id).unwrap();
      if (loanApplication.current_status?.code && loanApplication.current_status.code !== loanStatus) {
        statusPoll.setIsActiveInterval(false);
        navigate(`/${loanApplication.current_status.url}`);
      }
    } catch (error) {
      console.error("Error al consultar el estatus de la solicitud", error);
    }
  }, SECONDS_TO_FETCH);

  const { setIsActiveInterval } = statusPoll;

  useEffect(() => {
    setIsActiveInterval(true);
    return () => setIsActiveInterval(false);
  }, [setIsActiveInterval]);

  const telefono = accountUser?.phone
    ? `(${accountUser.code_phone}) ${formatPhoneNumber(String(accountUser.phone))}`
    : "<NOT_FOUND>";

  return (
    <Box>
      <PageHeader
        eyebrow="EXPEDIENTE COMPLETADO"
        title="Un ejecutivo se pondrá en contacto contigo"
        subtitle={`Recibimos tu solicitud y tu expediente de arrendamiento de ${EQUIPO_DEFAULT}. En las próximas 24 horas hábiles un ejecutivo te llamará para revisar la oferta, resolver tus dudas y acompañarte hasta la entrega del equipo.`}
      />

      <Paper variant="lendia_paper" sx={{ textAlign: "center", p: "36px 24px" }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          <Box component="img" src={iconCheckCircleSuccess} alt="" width={50} height={50} />
        </Box>
        <Typography
          component="h3"
          sx={{ fontSize: 19, fontWeight: 600, letterSpacing: "-0.01em", color: "primary.dark", mb: "6px" }}
        >
          Expediente completado
        </Typography>
        <Typography sx={{ fontSize: 13, color: "tokens.textTertiary", lineHeight: 1.55, mt: "10px", mb: "22px" }}>
          Te avisaremos por WhatsApp y correo.
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "14px",
            maxWidth: 440,
            mx: "auto",
            textAlign: "left",
          }}
        >
          <NextStep
            n={1}
            title="Llamada de tu ejecutivo"
            description={`Te contactará al ${telefono} para confirmar condiciones, anticipo y plazo.`}
          />
          <NextStep
            n={2}
            title="Expediente y cotización del equipo"
            description="Revisaremos los documentos que subiste y te pediremos la cotización del proveedor."
          />
          <NextStep
            n={3}
            title="Firma y entrega"
            description="Firmas el contrato de arrendamiento en línea y coordinamos la entrega con el proveedor."
          />
        </Box>
      </Paper>
    </Box>
  );
};
